import { Link } from "react-router-dom";
import { HeroFull } from '@/components/HeroFull';
import { HERO_IMAGE_DEFAULT } from '@/config/images';
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { NeighborCities } from "@/components/feature/NeighborCities";
import SEO from "@/components/feature/SEO";

const PUEBLOS_SERRANIA = [
  { name: "Júzcar", desc: "El \"pueblo pitufo\", pintado de azul. Persianas exteriores resistentes al frío serrano." },
  { name: "Pujerra", desc: "Pueblo al pie de la Sierra Bermeja. Persianas tradicionales en cascos históricos." },
  { name: "Igualeja", desc: "Pueblo blanco con manantial. Especialistas en mallorquinas tradicionales." },
  { name: "Cartajima", desc: "Pueblo a 845m de altitud. Persianas resistentes a heladas y viento de levante." },
  { name: "Faraján", desc: "Pueblo agrícola, conocido por sus castañares. Restauración de persianas de madera." },
  { name: "Alpandeire", desc: "Cuna de Fray Leopoldo. Trabajamos persianas en cortijos y vivienda local." },
  { name: "Gaucín", desc: "Mirador natural sobre el Estrecho. Persianas exteriores para chalets británicos." },
  { name: "Casares", desc: "Pueblo blanco colgado, cuna de Blas Infante. Restauración respetuosa." },
  { name: "Genalguacil", desc: "Pueblo museo al aire libre. Persianas tradicionales en casco histórico." },
  { name: "Benalauría", desc: "Pueblo morisco con casco medieval. Especialistas en persianas restauración." },
  { name: "Benarrabá", desc: "Pueblo serrano cerca del Genal. Persianas resistentes al viento serrano." },
];

export default function SerraniaRondaHub() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Persianas en la Serranía de Ronda | Pueblos blancos"
        description="Reparación e instalación de persianas en la Serranía de Ronda: Júzcar, Gaucín, Casares y pueblos blancos del Valle del Genal."
        canonical="/zonas/serrania-de-ronda/"
        ogType="website"
        breadcrumbs={[
          { name: "Inicio", path: "/" },
          { name: "Zonas", path: "/zonas" },
          { name: "Serranía de Ronda", path: "/zonas/serrania-de-ronda" },
        ]}
      />
      <Header />

      <main id="main-content">
        <Breadcrumbs
          items={[
            { name: "Inicio", path: "/" },
            { name: "Zonas", path: "/zonas" },
            { name: "Serranía de Ronda" },
          ]}
        />
        <HeroFull
          imageSrc={HERO_IMAGE_DEFAULT}
          imageAlt="Persianas Toldos Málaga atendiendo pueblos de la Serranía de Ronda"
          badge="⛰️ Cobertura Serranía de Ronda"
          title="Persianas, Toldos y Mosquiteras en la Serranía de Ronda"
          subtitle="Cubrimos Ronda, Júzcar, Gaucín, Casares y pueblos blancos del valle del Genal. Visitas agrupadas 1-2x/sem."
          primaryCta={{ href: "tel:+34951120770", label: "Llamar: 951 12 07 70" }}
          secondaryCta={{ href: "https://wa.me/34627213347", label: "💬 WhatsApp" }}
          trustSignals={["Visitas agrupadas 1-2x/sem", "Presupuesto gratis", "Garantía 2 años"]}
        />

        {/* ¿Qué es la Serranía de Ronda? */}
        <section className="bg-surface-alt py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
              ¿Qué es la Serranía de Ronda?
            </h2>
            <div className="max-w-3xl">
              <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed mb-4">
                La Serranía de Ronda es la comarca montañosa del oeste de Málaga, con su capital comarcal Ronda y una veintena de pueblos blancos repartidos entre el Valle del Genal y la Sierra Bermeja. Con altitudes que superan los 700m en muchos municipios, el clima es notablemente más frío que en la costa, con heladas frecuentes en invierno y nevadas ocasionales en cotas altas.
              </p>
              <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed">
                Esto exige persianas exteriores resistentes al frío y al viento serrano, siendo muy habituales las persianas mallorquinas tradicionales en cascos históricos y aluminio reforzado en segundas residencias modernas.
              </p>
            </div>
          </div>
        </section>

        {/* Pueblos cubiertos */}
        <section className="bg-white py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-6">
              Pueblos cubiertos en la Serranía
            </h2>
            <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed mb-8 max-w-3xl">
              Reparamos persianas en los pueblos blancos de la Serranía. Visitas agrupadas 1-2 veces por semana para optimizar desplazamiento.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl">
              {PUEBLOS_SERRANIA.map((p) => (
                <div
                  key={p.name}
                  className="bg-surface-alt rounded-2xl p-6 border border-border hover:border-primary transition-colors"
                >
                  <h3 className="font-heading font-bold text-base text-text-primary mb-2">{p.name}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 max-w-3xl">
              <p className="font-body text-base text-text-secondary leading-relaxed">
                La cabecera comarcal tiene city page dedicada:{" "}
                <Link to="/persianas/ronda" className="text-primary hover:underline font-semibold">Ronda</Link>.
              </p>
            </div>
          </div>
        </section>

        {/* Servicios */}
        <section className="bg-surface-alt py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-6">
              Servicios en toda la Serranía de Ronda
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl">
              <div className="bg-white rounded-2xl p-6 border border-border">
                <h3 className="font-heading font-bold text-lg text-text-primary mb-3">Persianas</h3>
                <ul className="space-y-2 font-body text-sm text-text-secondary">
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Reparación de motores tubulares</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Cambio de cinta o enrollador</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Sustitución de lamas (aluminio y PVC)</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Motorización de persianas manuales</li>
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-border">
                <h3 className="font-heading font-bold text-lg text-text-primary mb-3">Toldos</h3>
                <ul className="space-y-2 font-body text-sm text-text-secondary">
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Instalación toldos extensibles</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Toldos cofre motorizados</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Reparación de brazos y lonas</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Pérgolas bioclimáticas</li>
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-border">
                <h3 className="font-heading font-bold text-lg text-text-primary mb-3">Mosquiteras</h3>
                <ul className="space-y-2 font-body text-sm text-text-secondary">
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Mosquiteras enrollables</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Mosquiteras correderas</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Mosquiteras fijas a medida</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Cambio de tela en mosquiteras</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Tarifas */}
        <section className="bg-white py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
              Tarifas zona Serranía
            </h2>
            <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed mb-8 max-w-2xl">
              Precios aplicables a toda la comarca de la Serranía de Ronda. Visitas agrupadas para optimizar costes de desplazamiento.
            </p>
            <div className="max-w-2xl mx-auto bg-surface-alt rounded-3xl border border-border overflow-hidden">
              <div className="divide-y divide-border">
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Cambiar cinta persiana</span>
                  <span className="font-heading font-bold text-lg text-primary">75€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Reparar motor persiana</span>
                  <span className="font-heading font-bold text-lg text-primary">95€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Sustituir motor</span>
                  <span className="font-heading font-bold text-lg text-primary">165€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Cambiar lamas (por lama)</span>
                  <span className="font-heading font-bold text-lg text-primary">15€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Desplazamiento</span>
                  <span className="font-heading font-bold text-lg text-primary">+10-15€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Revisión + presupuesto</span>
                  <span className="font-heading font-bold text-lg text-green-600">GRATIS</span>
                </div>
              </div>
              <div className="px-6 md:px-8 py-4 bg-surface-alt border-t border-border">
                <p className="font-body text-sm text-text-secondary text-center">
                  La Serranía está a 1-1.5h de Málaga capital. Para optimizar coste de desplazamiento, agrupamos visitas en la misma jornada. Comunica si tienes vecinos interesados.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Particularidades del clima serrano */}
        <section className="bg-surface-alt py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
              Particularidades del clima serrano
            </h2>
            <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed max-w-3xl">
              En pueblos por encima de 700m de altitud, las heladas invernales pueden romper componentes plásticos de cintas y enrolladores. Recomendamos persianas de aluminio con guías reforzadas y motores con protección contra bajas temperaturas. En cascos históricos restauramos persianas mallorquinas de madera respetando la estética original.
            </p>
          </div>
        </section>

        <NeighborCities slug="serrania-de-ronda" service="persianas" />

        {/* CTA Final */}
        <section className="bg-white py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
                ¿Necesitas reparar tu persiana en la Serranía?
              </h2>
              <p className="font-body text-lg text-text-secondary mb-8">
                Llámanos al 951 12 07 70 o pide presupuesto online. Visitamos Júzcar, Gaucín, Casares y todos los pueblos de la comarca.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="tel:+34951120770"
                  className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg px-8 py-5 rounded-pill transition-colors whitespace-nowrap touch-target"
                >
                  <span aria-hidden="true">📞</span> 951 12 07 70
                </a>
                <a
                  href="https://wa.me/34627213347?text=Hola%2C%20me%20interesa%20info%20sobre%20persianas%20en%20la%20Serrania%20de%20Ronda"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-whatsapp hover:bg-[#128C7E] text-white font-body font-bold text-lg px-6 py-4 rounded-pill transition-colors whitespace-nowrap"
                >
                  <span aria-hidden="true">💬</span> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}